/**
 * Профили подсказок ведения (BACKLOG №5): тихий / вибро / голос.
 * Выбор хранится на устройстве; голос — speechSynthesis на языке интерфейса,
 * вибро — navigator.vibrate (iOS Safari молча игнорирует — честный no-op).
 * Логика «что анонсировать» отделена от side-эффектов и тестируема.
 */
import { formatDistance, getLang, tr } from '@app/i18n'
import type { RouteStep } from './route-progress'

export type AlertProfile = 'silent' | 'vibrate' | 'voice'

const STORE_KEY = 'nav-alerts'
const ORDER: readonly AlertProfile[] = ['silent', 'vibrate', 'voice']

/** Анонс поворота — когда до него ближе этого порога. */
const TURN_ANNOUNCE_M = 25

export function loadAlertProfile(): AlertProfile {
  const raw = localStorage.getItem(STORE_KEY)
  return raw === 'vibrate' || raw === 'voice' ? raw : 'silent'
}

export function cycleAlertProfile(current: AlertProfile): AlertProfile {
  const next = ORDER[(ORDER.indexOf(current) + 1) % ORDER.length] ?? 'silent'
  try {
    localStorage.setItem(STORE_KEY, next)
  } catch {
    // приватный режим — профиль не переживёт вкладку
  }
  return next
}

export type Announcement =
  | { readonly type: 'turn'; readonly step: RouteStep }
  | { readonly type: 'arrived' }

/**
 * Что анонсировать на этом апдейте прогресса: ближайший НЕанонсированный
 * поворот ближе TURN_ANNOUNCE_M впереди позиции, или прибытие. Чистая
 * функция от (progress, steps, announcedIndices) — состояние снаружи.
 */
export function nextAnnouncement(
  progress: { readonly matchedIndex: number; readonly arrived: boolean },
  steps: readonly RouteStep[],
  announced: ReadonlySet<number>,
  distanceDoneM: number,
): Announcement | null {
  if (progress.arrived) return announced.has(-1) ? null : { type: 'arrived' }
  for (const step of steps) {
    if (step.index <= progress.matchedIndex) continue
    if (announced.has(step.index)) continue
    const aheadM = step.distanceFromStartM - distanceDoneM
    if (aheadM >= 0 && aheadM <= TURN_ANNOUNCE_M) return { type: 'turn', step }
    if (aheadM > TURN_ANNOUNCE_M) break
  }
  return null
}

// ── Side-эффекты ─────────────────────────────────────────────────────────────

const BCP47: Readonly<Record<string, string>> = { ru: 'ru-RU', en: 'en-US', et: 'et-EE' }

function speak(text: string): void {
  try {
    const u = new SpeechSynthesisUtterance(text)
    u.lang = BCP47[getLang()] ?? 'en-US'
    speechSynthesis.cancel()
    speechSynthesis.speak(u)
  } catch {
    // без синтеза (старый браузер) — молча
  }
}

function vibrate(pattern: number[]): void {
  try {
    navigator.vibrate?.(pattern)
  } catch {
    // не поддерживается — молча
  }
}

/** Выполнить анонс выбранным профилем. */
export function deliver(a: Announcement, profile: AlertProfile): void {
  if (profile === 'silent') return
  if (profile === 'vibrate') {
    vibrate(a.type === 'arrived' ? [100, 60, 100, 60, 300] : [200])
    return
  }
  if (a.type === 'arrived') {
    speak(tr('follow.voice_arrived'))
    return
  }
  const dir = tr(a.step.turn === 'left' ? 'follow.voice_left' : 'follow.voice_right')
  speak(tr('follow.voice_turn', { dir, dist: formatDistance(TURN_ANNOUNCE_M) }))
}
